import React, { useContext } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Card, CardContent, Box, Chip, Divider, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import ChatIcon from '@mui/icons-material/Chat';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddIcon from '@mui/icons-material/Add';
import { PresenterListContext } from '../../context/PresenterListContext';
import { showToast } from '../commonUnit/Toast';

const InteractionDetailsModal = ({ open, onClose, interactions, timeRange }) => {
  const { codeList, setCodeList } = useContext(PresenterListContext);

  const getIntentColor = (intent) => {
    const colors = {
      'Learning': '#3b82f6',
      'Debugging': '#ef4444',
      'Exploring': '#f59e0b',
      'Validation': '#8b5cf6',
      'Completion': '#10b981',
    };
    return colors[intent] || '#6b7280';
  };

  const getQuestionTypeColor = (questionType) => {
    const colors = {
      'Task Clarification': '#6366f1',
      'Syntax Error': '#ec4899',
      'Implementation Challenge': '#f59e0b',
      'Failing Test Case': '#ef4444',
      'Code Refinement': '#10b981',
    };
    return colors[questionType] || '#6b7280';
  };

  const handleAddToPresenter = (interaction, index) => {
    if (!Array.isArray(codeList)) {
      console.log('codeList is not an array');
      return;
    }

    const sessionName = `Student ${interaction.user_id} - ${interaction.student_intent}`;
    
    // Create a code object similar to what's expected
    const codeObject = {
      content: interaction.code_snapshot || '# No code available',
      passrate: 0,
      creater_id: interaction.user_id,
      id: `interaction-${interaction.id}`,
    };

    // Structure the chat history from message_history
    const chatHistory = (interaction.message_history || []).map(msg => ({
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp || new Date(interaction.created_time).getTime(),
    }));

    const newCodeList = [...codeList];
    newCodeList.push({
      name: sessionName,
      code: codeObject,
      chatHistory: chatHistory,
      hasChat: chatHistory.length > 0,
    });
    
    setCodeList(newCodeList);
    showToast(`Added "${sessionName}" to Presenter View`, 'success', 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          maxHeight: '85vh',
          minWidth: '800px',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="h6">Student Interactions</Typography>
            <Typography variant="caption" color="textSecondary">
              Minute {timeRange.start}-{timeRange.end} • {interactions.length} {interactions.length === 1 ? 'interaction' : 'interactions'}
            </Typography>
          </div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {interactions.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">
              No interactions found for this time period
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {interactions.map((interaction, index) => (
              <Card 
                key={interaction.id}
                variant="outlined"
                sx={{ 
                  borderLeft: `4px solid ${getIntentColor(interaction.student_intent)}`,
                }}
              >
                <CardContent>
                  {/* Student Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="h6" fontWeight="bold">
                        Student {interaction.user_id}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddToPresenter(interaction, index)}
                        sx={{ textTransform: 'none' }}
                      >
                        Add to Presenter
                      </Button>
                    </Box>
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip 
                        label={interaction.question_type}
                        size="small"
                        sx={{ 
                          backgroundColor: getQuestionTypeColor(interaction.question_type),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                      <Chip 
                        label={interaction.student_intent}
                        size="small"
                        sx={{ 
                          backgroundColor: getIntentColor(interaction.student_intent),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {new Date(interaction.created_time).toLocaleTimeString()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Student's Intent Description */}
                  {interaction.student_facing_description && (
                    <Box 
                      sx={{ 
                        backgroundColor: '#fff3e0',
                        border: '1px solid #ffb74d',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        mb: 2
                      }}
                    >
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#e65100' }}>
                        💭 "{interaction.student_facing_description}"
                      </Typography>
                    </Box>
                  )}

                  {/* Split Layout: Code on Left, Chat on Right */}
                  <Box display="flex" gap={2} mb={3}>
                    {/* Left Side - Code */}
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CodeIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Code Snapshot:
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: '#f8f8f8',
                          borderRadius: '4px',
                          padding: '12px',
                          fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace',
                          fontSize: '13px',
                          whiteSpace: 'pre-wrap',
                          border: '1px solid #d1d5db',
                          minHeight: '200px',
                          lineHeight: '1.5',
                          height: '100%',
                          maxHeight: '400px',
                          overflow: 'auto',
                        }}
                      >
                        {interaction.code_snapshot ? interaction.code_snapshot : <em style={{ color: '#999', fontFamily: 'inherit' }}># No code written yet</em>}
                      </Box>
                    </Box>

                    {/* Right Side - Chat History */}
                    <Box flex={1} display="flex" flexDirection="column" gap={2}>
                      {interaction.message_history && interaction.message_history.length > 0 ? (
                        interaction.message_history.map((msg, msgIndex) => (
                          <Box key={msgIndex}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              {msg.role === 'user' ? (
                                <ChatIcon fontSize="small" color="primary" />
                              ) : (
                                <SmartToyIcon fontSize="small" sx={{ color: '#1976d2' }} />
                              )}
                              <Typography variant="subtitle2" fontWeight="bold">
                                {msg.role === 'user' ? 'Student Question:' : 'AI Response:'}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                backgroundColor: msg.role === 'user' ? '#ffffff' : '#f3f4f6',
                                padding: '12px 16px',
                                borderRadius: '12px',
                                border: '1px solid #e5e7eb',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {msg.content}
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box 
                          display="flex" 
                          alignItems="center" 
                          justifyContent="center" 
                          sx={{ 
                            minHeight: '200px',
                            color: '#999',
                            fontStyle: 'italic'
                          }}
                        >
                          No chat history available
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InteractionDetailsModal;